import React, { useState } from "react";
import classNames from "classnames";
import {
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  EyeIcon,
  EyeSlashIcon,
  PlusIcon,
  BuildingStorefrontIcon,
  TruckIcon,
  CheckCircleIcon,
  XMarkIcon,
  FunnelIcon,
  MapPinIcon,
  DocumentTextIcon,
  PhoneIcon,
  ShieldCheckIcon,
  ClockIcon,
  ArchiveBoxIcon,
  PrinterIcon,
} from "@heroicons/react/24/outline";
import { money, toFloat } from "./envois-shared";

export interface MarchandShipmentItem {
  id: string;
  freightRef: string;
  merchantName: string;
  merchantContact: string;
  merchantPhone: string;
  typeMarchand: "Supermarché & Grande Distribution" | "Grossiste Agro-alimentaire" | "Commerçant Import-Export" | "Quincaillerie & Matériaux";
  departureHub: string;
  destinationQuai: string;
  corridor: "Douala → Yaoundé" | "Douala → Garoua" | "Douala → Bafoussam" | "Douala → Kribi" | "Intra-urbain Douala";
  conditionnement: string;
  tonnage: number; // Tonnes
  nbPalettes: number;
  vehicleAssigned: "Semi-remorque 30T Bâché" | "Camion Frigorifique 10T" | "Camion Plateau Conteneur" | "Fourgon 5T Grand Volume";
  chauffeur: string;
  chauffeurPhone: string;
  bonCommandeNo: string;
  montantFretXaf: number;
  reglementStatus: "Facture fin de mois 30j" | "Acompte 50% réglé" | "Soldé à quai" | "Virement en attente";
  logisticStatus:
    | "Réservation fret confirmée"
    | "Chargement quai dépôt"
    | "Scellés posés & En convoi"
    | "Arrivée quai déchargement"
    | "Contrôle qualité & Déchargé"
    | "Émargé & Clôturé";
  temperatureControlee?: string;
  scelleNo: string;
  dateDepart: string;
  etaQuai: string;
  notes: string;
}

const SEED_MARCHAND_SHIPMENTS: MarchandShipmentItem[] = [
  {
    id: "m1",
    freightRef: "MCH-2609-DOVV-0081",
    merchantName: "Supermarchés DOVV",
    merchantContact: "M. Tagne Roger (Dir. Approvisionnement)",
    merchantPhone: "+237 699 22 11 00",
    typeMarchand: "Supermarché & Grande Distribution",
    departureHub: "Dépôt Central Bonabéri Douala",
    destinationQuai: "Supermarché DOVV Yaoundé Bastos - Quai Déchargement",
    corridor: "Douala → Yaoundé",
    conditionnement: "14 Palettes Europe Denrées alimentaires & Huiles",
    tonnage: 8.5,
    nbPalettes: 14,
    vehicleAssigned: "Semi-remorque 30T Bâché",
    chauffeur: "Jean-Paul Etoundi (LT 8921 AB)",
    chauffeurPhone: "+237 677 44 22 11",
    bonCommandeNo: "BC-2026-DOVV-441",
    montantFretXaf: 580000,
    reglementStatus: "Facture fin de mois 30j",
    logisticStatus: "Scellés posés & En convoi",
    temperatureControlee: "Ambiant sec garanti",
    scelleNo: "SCL-DLA-8819",
    dateDepart: "Aujourd'hui · 06:00",
    etaQuai: "Aujourd'hui 15:30 (Quai Bastos)",
    notes: "Priorité déchargement quai 1 DOVV Bastos dès arrivée.",
  },
  {
    id: "m2",
    freightRef: "MCH-2609-CRF-0084",
    merchantName: "Carrefour Market Cameroun",
    merchantContact: "Mme Bella Simone (Cheffe Logistique Frais)",
    merchantPhone: "+237 694 55 66 77",
    typeMarchand: "Supermarché & Grande Distribution",
    departureHub: "Plateforme Logistique Bassa Douala",
    destinationQuai: "Carrefour Market Douala Akwa - Réception Frais",
    corridor: "Intra-urbain Douala",
    conditionnement: "6 Palettes Produits Frais & Produits Laitiers",
    tonnage: 3.2,
    nbPalettes: 6,
    vehicleAssigned: "Camion Frigorifique 10T",
    chauffeur: "Mamadou Bello (CE 4410 OC)",
    chauffeurPhone: "+237 655 11 00 22",
    bonCommandeNo: "BC-CRF-2026-902",
    montantFretXaf: 180000,
    reglementStatus: "Facture fin de mois 30j",
    logisticStatus: "Contrôle qualité & Déchargé",
    temperatureControlee: "+3.5°C certifié thermo-tracker",
    scelleNo: "SCL-FRIG-0042",
    dateDepart: "Aujourd'hui · 08:30",
    etaQuai: "Livré à 10:15",
    notes: "Chaîne du froid validée. Température conforme.",
  },
  {
    id: "m3",
    freightRef: "MCH-2609-GAR-0089",
    merchantName: "Comptoir Grossiste Alhadji Oumarou Garoua",
    merchantContact: "El Hadj Oumarou (Gérant)",
    merchantPhone: "+237 671 88 99 00",
    typeMarchand: "Grossiste Agro-alimentaire",
    departureHub: "Port Autonome de Douala - Magasin 14",
    destinationQuai: "Entrepôt Central Garoua Plateau",
    corridor: "Douala → Garoua",
    conditionnement: "800 Sacs Farine & Riz importé (26 Tonnes)",
    tonnage: 26.0,
    nbPalettes: 28,
    vehicleAssigned: "Semi-remorque 30T Bâché",
    chauffeur: "Ibrahim D. (LT 9901 NO)",
    chauffeurPhone: "+237 690 33 22 11",
    bonCommandeNo: "BC-GAR-2026-119",
    montantFretXaf: 1450000,
    reglementStatus: "Acompte 50% réglé",
    logisticStatus: "Scellés posés & En convoi",
    scelleNo: "SCL-PORT-9941",
    dateDepart: "Hier · 18:00",
    etaQuai: "Demain 18:00 (Garoua)",
    notes: "Convoi lourd fret Nord. Pesée essieux validée au pont-bascule.",
  },
  {
    id: "m4",
    freightRef: "MCH-2609-SPR-0093",
    merchantName: "SPAR Supermarchés",
    merchantContact: "Marcelle N. (Directrice Achats)",
    merchantPhone: "+237 677 00 11 22",
    typeMarchand: "Supermarché & Grande Distribution",
    departureHub: "Entrepôt Central Douala Bonanjo",
    destinationQuai: "SPAR Bonapriso Quai Réception",
    corridor: "Intra-urbain Douala",
    conditionnement: "8 Palettes Boissons, Vins & Épicerie Fine",
    tonnage: 4.5,
    nbPalettes: 8,
    vehicleAssigned: "Fourgon 5T Grand Volume",
    chauffeur: "Cédric E. (LT 2244 AA)",
    chauffeurPhone: "+237 696 22 33 44",
    bonCommandeNo: "BC-SPAR-2026-081",
    montantFretXaf: 220000,
    reglementStatus: "Soldé à quai",
    logisticStatus: "Arrivée quai déchargement",
    scelleNo: "SCL-SPAR-104",
    dateDepart: "Aujourd'hui · 10:00",
    etaQuai: "Quai en cours de contrôle",
    notes: "Déchargement au transpalette électrique.",
  },
  {
    id: "m5",
    freightRef: "MCH-2609-QNC-0097",
    merchantName: "Quincaillerie Générale du Cameroun",
    merchantContact: "Alain Foing (Resp. Logistique)",
    merchantPhone: "+237 699 44 88 22",
    typeMarchand: "Quincaillerie & Matériaux",
    departureHub: "Zone Industrielle Bassa",
    destinationQuai: "Dépôt Quincaillerie Bafoussam Ville",
    corridor: "Douala → Bafoussam",
    conditionnement: "Fers à béton, Tôles bac & Ciment spécial",
    tonnage: 18.0,
    nbPalettes: 16,
    vehicleAssigned: "Camion Plateau Conteneur",
    chauffeur: "Gervais M. (OU 3388 BB)",
    chauffeurPhone: "+237 655 99 88 77",
    bonCommandeNo: "BC-QNC-2026-781",
    montantFretXaf: 820000,
    reglementStatus: "Virement en attente",
    logisticStatus: "Chargement quai dépôt",
    scelleNo: "SCL-BTP-552",
    dateDepart: "Aujourd'hui · 14:00",
    etaQuai: "Ce soir 20:00 (Bafoussam)",
    notes: "Arrimage et sangles haute résistance requis.",
  },
];

export default function MarchandConsole() {
  const [data, setData] = useState<MarchandShipmentItem[]>(SEED_MARCHAND_SHIPMENTS);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("Tous");
  const [corridorFilter, setCorridorFilter] = useState("Tous");
  const [statusFilter, setStatusFilter] = useState("Tous");
  const [view, setView] = useState<"table" | "kanban" | "flotte">("table");

  // Display toggles
  const [isFullScreenPage, setIsFullScreenPage] = useState(false);
  const [isFullScreenTable, setIsFullScreenTable] = useState(false);
  const [showKpiCards, setShowKpiCards] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  // Modals
  const [newOrderModal, setNewOrderModal] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState<MarchandShipmentItem | null>(null);
  const [emargementModalItem, setEmargementModalItem] = useState<MarchandShipmentItem | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);

  // New shipment form state
  const [newShipment, setNewShipment] = useState({
    merchantName: "",
    merchantContact: "",
    merchantPhone: "",
    typeMarchand: "Supermarché & Grande Distribution" as MarchandShipmentItem["typeMarchand"],
    departureHub: "Dépôt Central Bonabéri Douala",
    destinationQuai: "",
    corridor: "Douala → Yaoundé" as MarchandShipmentItem["corridor"],
    conditionnement: "",
    tonnage: 5.0,
    nbPalettes: 10,
    vehicleAssigned: "Semi-remorque 30T Bâché" as MarchandShipmentItem["vehicleAssigned"],
    chauffeur: "Chauffeur Partenaire Flotte",
    chauffeurPhone: "+237 677 00 00 00",
    bonCommandeNo: "",
    montantFretXaf: 450000,
    reglementStatus: "Facture fin de mois 30j" as MarchandShipmentItem["reglementStatus"],
    temperatureControlee: "Ambiant standard",
    notes: "",
  });

  const notify = (msg: string) => {
    setFeedback(msg);
    setTimeout(() => setFeedback(null), 3500);
  };

  const filtered = data.filter((item) => {
    if (typeFilter !== "Tous" && item.typeMarchand !== typeFilter) return false;
    if (corridorFilter !== "Tous" && item.corridor !== corridorFilter) return false;
    if (statusFilter !== "Tous" && item.logisticStatus !== statusFilter) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      const match =
        item.freightRef.toLowerCase().includes(q) ||
        item.merchantName.toLowerCase().includes(q) ||
        item.bonCommandeNo.toLowerCase().includes(q) ||
        item.destinationQuai.toLowerCase().includes(q) ||
        item.chauffeur.toLowerCase().includes(q) ||
        item.conditionnement.toLowerCase().includes(q);
      if (!match) return false;
    }
    return true;
  });

  // KPIs
  const totalExpeditions = data.length;
  const totalTonnage = data.reduce((acc, curr) => acc + toFloat(curr.tonnage), 0);
  const totalPalettes = data.reduce((acc, curr) => acc + toFloat(curr.nbPalettes), 0);
  const totalFretCa = data.reduce((acc, curr) => acc + toFloat(curr.montantFretXaf), 0);
  const enConvoi = data.filter((d) => d.logisticStatus === "Scellés posés & En convoi").length;

  const handleCreateShipment = (e: React.FormEvent) => {
    e.preventDefault();
    const item: MarchandShipmentItem = {
      id: `m${Date.now()}`,
      freightRef: `MCH-2609-${newShipment.merchantName.slice(0, 3).toUpperCase()}-00${data.length + 1}`,
      merchantName: newShipment.merchantName || "Marchand Partenaire",
      merchantContact: newShipment.merchantContact || "Resp. Quai",
      merchantPhone: newShipment.merchantPhone || "+237 600 00 00 00",
      typeMarchand: newShipment.typeMarchand,
      departureHub: newShipment.departureHub,
      destinationQuai: newShipment.destinationQuai || "Quai de déchargement",
      corridor: newShipment.corridor,
      conditionnement: newShipment.conditionnement || "Palettes marchandises",
      tonnage: Number(newShipment.tonnage) || 5,
      nbPalettes: Number(newShipment.nbPalettes) || 10,
      vehicleAssigned: newShipment.vehicleAssigned,
      chauffeur: newShipment.chauffeur,
      chauffeurPhone: newShipment.chauffeurPhone,
      bonCommandeNo: newShipment.bonCommandeNo || `BC-${Date.now().toString().slice(-5)}`,
      montantFretXaf: Number(newShipment.montantFretXaf) || 400000,
      reglementStatus: newShipment.reglementStatus,
      logisticStatus: "Réservation fret confirmée",
      scelleNo: `SCL-${Math.floor(1000 + Math.random() * 9000)}`,
      dateDepart: "Aujourd'hui · Programmé",
      etaQuai: "Sous 24h",
      notes: newShipment.notes,
    };
    setData([item, ...data]);
    notify(`Ordre de transport marchand créé avec succès : ${item.freightRef}`);
    setNewOrderModal(false);
  };

  const handleValiderEmargement = () => {
    if (!emargementModalItem) return;
    setData((prev) =>
      prev.map((d) =>
        d.id === emargementModalItem.id
          ? { ...d, logisticStatus: "Émargé & Clôturé", reglementStatus: "Soldé à quai" }
          : d
      )
    );
    notify(`Bon de livraison marchand émargé & clôturé pour ${emargementModalItem.freightRef}`);
    setEmargementModalItem(null);
  };

  const kanbanLanes: { title: string; status: MarchandShipmentItem["logisticStatus"]; badge: string }[] = [
    { title: "Réservé / Préparation", status: "Réservation fret confirmée", badge: "bg-gray-100 text-gray-800" },
    { title: "Chargement quai dépôt", status: "Chargement quai dépôt", badge: "bg-amber-100 text-amber-800" },
    { title: "En convoi routier", status: "Scellés posés & En convoi", badge: "bg-blue-100 text-blue-800" },
    { title: "Arrivée quai livraison", status: "Arrivée quai déchargement", badge: "bg-purple-100 text-purple-800" },
    { title: "Déchargé & Contrôlé", status: "Contrôle qualité & Déchargé", badge: "bg-emerald-100 text-emerald-800" },
    { title: "Émargé & Clôturé", status: "Émargé & Clôturé", badge: "bg-emerald-200 text-emerald-900" },
  ];

  return (
    <div
      className={classNames(
        "h-full flex flex-col bg-[#faf7ff] text-[#1f1b2e] overflow-hidden transition-all",
        isFullScreenPage ? "fixed inset-0 z-50 p-6 bg-[#faf7ff]" : "p-6 lg:p-7"
      )}
    >
      {/* Toast Alert */}
      {feedback && (
        <div className="fixed top-14 right-6 z-50 bg-[#1f1b2e] text-white px-4 py-2.5 rounded-xl shadow-xl flex items-center gap-2.5 text-xs animate-fade-in border border-purple-400/30">
          <CheckCircleIcon className="w-4 h-4 text-emerald-400 shrink-0" />
          <span>{feedback}</span>
        </div>
      )}

      {/* Header bar */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4 shrink-0">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-tr from-amber-600 to-orange-500 text-white flex items-center justify-center shadow-xs">
              <BuildingStorefrontIcon className="w-5 h-5" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-[#1f1b2e]">
              Expéditions Marchands & Supermarchés (Fret B2B)
            </h1>

            {/* Plein écran Page */}
            <button
              type="button"
              onClick={() => setIsFullScreenPage(!isFullScreenPage)}
              title={isFullScreenPage ? "Quitter le plein écran" : "Plein écran sur toute la page"}
              className="p-1.5 rounded-lg border border-purple-200 bg-white hover:bg-purple-50 text-purple-800 text-xs font-semibold flex items-center gap-1 shadow-2xs cursor-pointer"
            >
              {isFullScreenPage ? (
                <>
                  <ArrowsPointingInIcon className="w-3.5 h-3.5" />
                  <span>Réduire</span>
                </>
              ) : (
                <>
                  <ArrowsPointingOutIcon className="w-3.5 h-3.5" />
                  <span>Plein écran</span>
                </>
              )}
            </button>

            {/* Masquer / Afficher stats */}
            <button
              type="button"
              onClick={() => setShowKpiCards(!showKpiCards)}
              title={showKpiCards ? "Masquer les statistiques" : "Afficher les statistiques"}
              className="p-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 text-xs font-semibold flex items-center gap-1 shadow-2xs cursor-pointer"
            >
              {showKpiCards ? (
                <>
                  <EyeSlashIcon className="w-3.5 h-3.5" />
                  <span>Masquer stats</span>
                </>
              ) : (
                <>
                  <EyeIcon className="w-3.5 h-3.5" />
                  <span>Afficher stats</span>
                </>
              )}
            </button>
          </div>
          <p className="text-xs text-[#6b6480] mt-1 font-medium">
            Réapprovisionnement des supermarchés (Dovv, Carrefour, Spar), grossistes et commerçants · Convois poids lourds, palettes & chaîne du froid
          </p>
        </div>

        {/* Action Buttons Top */}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => notify(`Bordereau récapitulatif fret généré (${filtered.length} expéditions)`)}
            className="flex items-center gap-1.5 h-9 px-3 border border-slate-300 bg-white hover:bg-slate-50 rounded-lg text-xs font-semibold text-slate-700 shadow-xs cursor-pointer"
          >
            <span className="w-2 h-2 rounded-xs bg-emerald-600" />
            <span>Export Fret B2B</span>
          </button>
          <button
            type="button"
            onClick={() => setNewOrderModal(true)}
            className="flex items-center gap-1.5 h-9 px-3.5 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white rounded-lg text-xs font-bold shadow-xs cursor-pointer transition-transform hover:scale-102"
          >
            <PlusIcon className="w-4 h-4 stroke-2" />
            <span>Nouvelle expédition marchand</span>
          </button>
        </div>
      </div>

      {/* KPI Cards (Collapsible) */}
      {showKpiCards && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3.5 shrink-0 animate-fade-in">
          <div className="p-3 bg-white border border-[#ece7f8] rounded-xl shadow-xs">
            <div className="text-[10px] font-bold tracking-wider uppercase text-[#8b81a8]">
              Expéditions Marchand
            </div>
            <div className="text-xl font-bold text-amber-700 mt-1">{totalExpeditions}</div>
            <div className="text-[11px] text-[#9b93b3]">convois supermarchés & grossistes</div>
          </div>
          <div className="p-3 bg-white border border-[#ece7f8] rounded-xl shadow-xs">
            <div className="text-[10px] font-bold tracking-wider uppercase text-[#8b81a8]">
              Tonnage Fret Actif
            </div>
            <div className="text-xl font-bold text-blue-700 mt-1">{totalTonnage.toFixed(1)} T</div>
            <div className="text-[11px] text-[#9b93b3]">{totalPalettes} palettes Europe</div>
          </div>
          <div className="p-3 bg-white border border-[#ece7f8] rounded-xl shadow-xs">
            <div className="text-[10px] font-bold tracking-wider uppercase text-[#8b81a8]">
              En Convoi Routier
            </div>
            <div className="text-xl font-bold text-purple-700 mt-1">{enConvoi}</div>
            <div className="text-[11px] text-[#9b93b3]">poids lourds en trajet</div>
          </div>
          <div className="p-3 bg-white border border-[#ece7f8] rounded-xl shadow-xs">
            <div className="text-[10px] font-bold tracking-wider uppercase text-[#8b81a8]">
              Taux Qualité Quai
            </div>
            <div className="text-xl font-bold text-emerald-700 mt-1">98.5 %</div>
            <div className="text-[11px] text-[#9b93b3]">scellés conformes sans avarie</div>
          </div>
          <div className="p-3 bg-white border border-[#ece7f8] rounded-xl shadow-xs">
            <div className="text-[10px] font-bold tracking-wider uppercase text-[#8b81a8]">
              Volume d'Affaires Fret
            </div>
            <div className="text-xl font-bold text-[#241f36] mt-1">
              {money(totalFretCa)}
            </div>
            <div className="text-[11px] text-[#9b93b3]">contrats transport B2B</div>
          </div>
        </div>
      )}

      {/* Control Bar: View Switcher, Search, Filter Toggle */}
      <div className="flex items-center gap-3 flex-wrap mb-3 shrink-0">
        <div className="flex p-0.5 bg-[#f1ecfe] rounded-lg gap-1">
          <button
            type="button"
            onClick={() => setView("table")}
            className={classNames(
              "px-3 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer",
              view === "table" ? "bg-white text-purple-900 shadow-xs" : "text-[#7c729b] hover:text-purple-900"
            )}
          >
            Tableau des convois
          </button>
          <button
            type="button"
            onClick={() => setView("kanban")}
            className={classNames(
              "px-3 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer",
              view === "kanban" ? "bg-white text-purple-900 shadow-xs" : "text-[#7c729b] hover:text-purple-900"
            )}
          >
            Pipeline Quais & Déchargements
          </button>
          <button
            type="button"
            onClick={() => setView("flotte")}
            className={classNames(
              "px-3 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer",
              view === "flotte" ? "bg-white text-purple-900 shadow-xs" : "text-[#7c729b] hover:text-purple-900"
            )}
          >
            Véhicules & Camions B2B
          </button>
        </div>

        {/* Search input */}
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Réf fret, supermarché (Dovv, Carrefour...), chauffeur, quai, bon commande..."
          className="flex-1 min-w-[260px] h-9 px-3.5 border border-purple-200/90 rounded-lg bg-white text-xs text-[#1f1b2e] focus:outline-none focus:ring-2 focus:ring-purple-400"
        />

        {/* Quick Corridor Filter */}
        <div className="flex items-center gap-1 text-xs">
          {["Tous", "Douala → Yaoundé", "Douala → Garoua", "Intra-urbain Douala"].map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCorridorFilter(c)}
              className={classNames(
                "h-8 px-2.5 rounded-lg border text-xs font-semibold transition-colors cursor-pointer",
                corridorFilter === c
                  ? "bg-purple-700 text-white border-purple-700 shadow-xs"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-purple-50"
              )}
            >
              {c}
            </button>
          ))}
        </div>

        {/* Toggle Filters panel */}
        <button
          type="button"
          onClick={() => setShowFilters(!showFilters)}
          className="h-9 px-3.5 border border-purple-200 bg-purple-50 hover:bg-purple-100 rounded-lg text-xs font-semibold text-purple-900 transition-colors cursor-pointer flex items-center gap-1.5"
        >
          <FunnelIcon className="w-3.5 h-3.5" />
          <span>{showFilters ? "Masquer filtres" : "Plus de filtres"}</span>
        </button>
      </div>

      {/* Expanded Filters Panel */}
      {showFilters && (
        <div className="p-3.5 bg-white border border-[#ede9fe] rounded-xl mb-3 shrink-0 shadow-xs animate-fade-in">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <div>
              <span className="block text-[10px] font-bold text-[#8b81a8] uppercase mb-1">Type d'enseigne</span>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="w-full h-8 px-2 border border-gray-200 rounded-md text-xs bg-white"
              >
                <option value="Tous">Toutes les enseignes</option>
                <option value="Supermarché & Grande Distribution">Supermarchés</option>
                <option value="Grossiste Agro-alimentaire">Grossistes</option>
                <option value="Quincaillerie & Matériaux">Quincaillerie / BTP</option>
              </select>
            </div>
            <div>
              <span className="block text-[10px] font-bold text-[#8b81a8] uppercase mb-1">Statut Logistique</span>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full h-8 px-2 border border-gray-200 rounded-md text-xs bg-white"
              >
                <option value="Tous">Tous les statuts</option>
                <option value="Réservation fret confirmée">Réservation fret confirmée</option>
                <option value="Chargement quai dépôt">Chargement quai dépôt</option>
                <option value="Scellés posés & En convoi">En convoi</option>
                <option value="Arrivée quai déchargement">Arrivée quai</option>
                <option value="Contrôle qualité & Déchargé">Déchargé</option>
                <option value="Émargé & Clôturé">Émargé & Clôturé</option>
              </select>
            </div>
            <div>
              <span className="block text-[10px] font-bold text-[#8b81a8] uppercase mb-1">Corridor B2B</span>
              <select
                value={corridorFilter}
                onChange={(e) => setCorridorFilter(e.target.value)}
                className="w-full h-8 px-2 border border-gray-200 rounded-md text-xs bg-white"
              >
                <option value="Tous">Tous les corridors</option>
                <option value="Douala → Yaoundé">Douala → Yaoundé</option>
                <option value="Douala → Garoua">Douala → Garoua (Nord)</option>
                <option value="Douala → Bafoussam">Douala → Bafoussam</option>
                <option value="Intra-urbain Douala">Intra-urbain Douala</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                type="button"
                onClick={() => {
                  setTypeFilter("Tous");
                  setCorridorFilter("Tous");
                  setStatusFilter("Tous");
                  setSearch("");
                }}
                className="w-full h-8 px-3 border border-gray-200 hover:bg-gray-100 rounded-md text-xs font-semibold text-gray-700"
              >
                Réinitialiser
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div
        className={classNames(
          "flex-1 min-h-0 overflow-hidden flex flex-col bg-white border border-[#ece7f8] rounded-xl shadow-xs transition-all",
          isFullScreenTable ? "fixed inset-0 z-50 p-6 bg-[#faf7ff]" : ""
        )}
      >
        {/* Table / View Sub-Header */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-[#fbf9ff] border-b border-[#ece7f8] text-xs shrink-0">
          <div className="flex items-center gap-2">
            <span className="font-bold text-[#1f1b2e]">Expéditions Marchands & Supermarchés</span>
            <span className="px-2 py-0.5 bg-amber-100 text-amber-900 rounded-full font-bold text-[11px]">
              {filtered.length} convois
            </span>
            <span className="text-[11px] text-gray-500 hidden sm:inline">
              (Cliquez pour voir le bon de commande, scellés ou émarger le déchargement)
            </span>
          </div>

          {/* Fullscreen Table Toggle */}
          <button
            type="button"
            onClick={() => setIsFullScreenTable(!isFullScreenTable)}
            className="p-1 rounded-md border border-gray-200 bg-white hover:bg-gray-100 text-gray-700 text-xs font-semibold flex items-center gap-1 cursor-pointer"
            title={isFullScreenTable ? "Réduire le tableau" : "Agrandir le tableau en plein écran"}
          >
            {isFullScreenTable ? (
              <>
                <ArrowsPointingInIcon className="w-3.5 h-3.5" />
                <span>Plein écran tableau (fermer)</span>
              </>
            ) : (
              <>
                <ArrowsPointingOutIcon className="w-3.5 h-3.5" />
                <span>Plein écran tableau</span>
              </>
            )}
          </button>
        </div>

        {/* View 1: Table View */}
        {view === "table" && (
          <div className="flex-1 overflow-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead className="bg-[#fbf9ff] sticky top-0 z-10 border-b border-[#ece7f8] text-[#7c729b] font-semibold text-[11px]">
                <tr>
                  <th className="py-2.5 px-3">Réf / Départ</th>
                  <th className="py-2.5 px-3">Marchand / Supermarché</th>
                  <th className="py-2.5 px-3">Conditionnement & Tonnage</th>
                  <th className="py-2.5 px-3">Trajet & Quai de Livraison</th>
                  <th className="py-2.5 px-3">Camion & Chauffeur</th>
                  <th className="py-2.5 px-3">Montant Fret</th>
                  <th className="py-2.5 px-3">Statut Logistique</th>
                  <th className="py-2.5 px-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-gray-800">
                {filtered.map((item) => (
                  <tr
                    key={item.id}
                    onClick={() => setSelectedShipment(item)}
                    className="hover:bg-purple-50/50 cursor-pointer transition-colors"
                  >
                    <td className="py-2.5 px-3 font-semibold text-purple-950 whitespace-nowrap">
                      <div>{item.freightRef}</div>
                      <div className="text-[10px] text-gray-400 font-normal">{item.dateDepart}</div>
                    </td>
                    <td className="py-2.5 px-3">
                      <div className="font-bold text-gray-900">{item.merchantName}</div>
                      <div className="text-[10px] text-gray-500 font-mono">BC: {item.bonCommandeNo}</div>
                    </td>
                    <td className="py-2.5 px-3">
                      <div className="font-medium text-gray-900 truncate max-w-[200px]" title={item.conditionnement}>
                        {item.conditionnement}
                      </div>
                      <div className="text-[10px] text-blue-700 font-semibold">
                        {item.tonnage} Tonnes · {item.nbPalettes} Palettes
                      </div>
                    </td>
                    <td className="py-2.5 px-3">
                      <div className="font-semibold text-gray-900">{item.corridor}</div>
                      <div className="text-[10px] text-gray-500 truncate max-w-[180px]">{item.destinationQuai}</div>
                    </td>
                    <td className="py-2.5 px-3 whitespace-nowrap">
                      <div className="text-gray-900 font-medium">{item.vehicleAssigned}</div>
                      <div className="text-[10px] text-gray-500">{item.chauffeur}</div>
                    </td>
                    <td className="py-2.5 px-3 whitespace-nowrap">
                      <div className="font-bold text-emerald-700">{money(item.montantFretXaf)}</div>
                      <div className="text-[10px] text-gray-400">{item.reglementStatus}</div>
                    </td>
                    <td className="py-2.5 px-3 whitespace-nowrap">
                      <span
                        className={classNames(
                          "px-2 py-0.5 rounded-full text-[10px] font-bold inline-block",
                          item.logisticStatus === "Émargé & Clôturé" || item.logisticStatus === "Contrôle qualité & Déchargé"
                            ? "bg-emerald-100 text-emerald-800"
                            : item.logisticStatus === "Arrivée quai déchargement"
                            ? "bg-purple-100 text-purple-800"
                            : item.logisticStatus === "Scellés posés & En convoi"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-amber-100 text-amber-800"
                        )}
                      >
                        {item.logisticStatus}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-right whitespace-nowrap">
                      <div className="flex items-center justify-end gap-1.5">
                        {item.logisticStatus !== "Émargé & Clôturé" && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEmargementModalItem(item);
                            }}
                            className="px-2 py-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-[10px] rounded shadow-2xs"
                          >
                            Émarger
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedShipment(item);
                          }}
                          className="px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-[10px] rounded"
                        >
                          Détails
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* View 2: Kanban Pipeline */}
        {view === "kanban" && (
          <div className="flex-1 overflow-x-auto p-4 bg-[#fbf9ff]">
            <div className="flex gap-3.5 min-w-[1100px] h-full">
              {kanbanLanes.map((lane) => {
                const laneShipments = filtered.filter((d) => d.logisticStatus === lane.status);
                return (
                  <div
                    key={lane.status}
                    className="flex-1 flex flex-col rounded-xl border border-gray-200 bg-white/70 p-3 min-w-[210px] max-w-[260px]"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-bold text-xs text-gray-800 truncate" title={lane.title}>
                        {lane.title}
                      </span>
                      <span className={classNames("px-2 py-0.5 rounded-full text-[10px] font-bold", lane.badge)}>
                        {laneShipments.length}
                      </span>
                    </div>

                    <div className="flex-1 overflow-y-auto space-y-2.5 pr-1">
                      {laneShipments.map((item) => (
                        <div
                          key={item.id}
                          onClick={() => setSelectedShipment(item)}
                          className="p-3 bg-white rounded-lg border border-gray-200 shadow-2xs hover:shadow-md transition-shadow cursor-pointer"
                        >
                          <div className="flex items-center justify-between text-[11px] mb-1">
                            <span className="font-bold text-purple-900">{item.merchantName}</span>
                            <span className="text-gray-400 text-[10px]">{item.tonnage} T</span>
                          </div>
                          <div className="text-xs font-semibold text-gray-800 truncate">{item.corridor}</div>
                          <div className="text-[11px] text-gray-500 truncate mt-0.5">{item.destinationQuai}</div>
                          <div className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-between text-[10px]">
                            <span className="font-mono text-gray-400">{item.scelleNo}</span>
                            <span className="font-bold text-emerald-700">{money(item.montantFretXaf)}</span>
                          </div>
                        </div>
                      ))}
                      {laneShipments.length === 0 && (
                        <div className="h-20 flex items-center justify-center text-xs text-gray-400 border border-dashed border-gray-200 rounded-lg">
                          Aucun convoi
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* View 3: Fleet & Camions B2B */}
        {view === "flotte" && (
          <div className="flex-1 overflow-auto p-5 bg-[#fbf9ff]">
            <h2 className="text-sm font-bold text-gray-900 mb-3">Flotte Poids Lourds & Semi-Remorques B2B</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { type: "Semi-remorque 30T Bâché", immat: "LT 8921 AB", driver: "Jean-Paul Etoundi", mission: "DOVV Yaoundé", statut: "En route", tonnageMax: "30 Tonnes" },
                { type: "Camion Frigorifique 10T", immat: "CE 4410 OC", driver: "Mamadou Bello", mission: "Carrefour Akwa Frais", statut: "À quai", tonnageMax: "10 Tonnes" },
                { type: "Semi-remorque 30T Bâché", immat: "LT 9901 NO", driver: "Ibrahim D.", mission: "Garoua Convoi Farine", statut: "En route", tonnageMax: "30 Tonnes" },
                { type: "Fourgon 5T Grand Volume", immat: "LT 2244 AA", driver: "Cédric E.", mission: "SPAR Bonapriso", statut: "Déchargement", tonnageMax: "5 Tonnes" },
                { type: "Camion Plateau Conteneur", immat: "OU 3388 BB", driver: "Gervais M.", mission: "Bafoussam Matériaux", statut: "En chargement", tonnageMax: "25 Tonnes" },
              ].map((camion, idx) => (
                <div key={idx} className="p-4 bg-white border border-[#ece7f8] rounded-xl shadow-xs">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-bold text-gray-900 text-sm">{camion.type}</h3>
                      <div className="text-xs text-purple-700 font-mono mt-0.5">{camion.immat}</div>
                    </div>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-800">
                      {camion.statut}
                    </span>
                  </div>
                  <div className="mt-3 text-xs text-gray-600">
                    <div>Chauffeur : <strong>{camion.driver}</strong></div>
                    <div>Mission actuelle : <strong className="text-gray-900">{camion.mission}</strong></div>
                    <div>Capacité utile : <strong className="text-emerald-700">{camion.tonnageMax}</strong></div>
                  </div>
                  <button
                    type="button"
                    onClick={() => notify(`Contact radio / tél établi avec le chauffeur ${camion.driver}`)}
                    className="mt-3 w-full py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-900 font-semibold text-xs rounded-lg cursor-pointer"
                  >
                    Contacter chauffeur convoi
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Modal: Émarger Déchargement Quai */}
      {emargementModalItem && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl border border-gray-200 animate-fade-in">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <ShieldCheckIcon className="w-5 h-5 text-emerald-600" />
                <h3 className="font-bold text-gray-900">Émargement Quai de Livraison</h3>
              </div>
              <button type="button" onClick={() => setEmargementModalItem(null)} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <p className="text-xs text-gray-600 mb-3">
              Certifier la conformité de réception des <strong>{emargementModalItem.nbPalettes} palettes ({emargementModalItem.tonnage} T)</strong> chez <strong>{emargementModalItem.merchantName}</strong>.
            </p>

            <div className="p-3 bg-amber-50/70 border border-amber-200 rounded-xl text-xs space-y-1 text-amber-950 mb-4">
              <div>N° Scellé à contrôler : <strong>{emargementModalItem.scelleNo}</strong></div>
              <div>Bon de Commande : <strong>{emargementModalItem.bonCommandeNo}</strong></div>
              <div>Chauffeur livreur : <strong>{emargementModalItem.chauffeur}</strong></div>
              {emargementModalItem.temperatureControlee && (
                <div>Chaîne de froid : <strong>{emargementModalItem.temperatureControlee}</strong></div>
              )}
            </div>

            <div className="p-3 bg-gray-50 border border-dashed border-gray-300 rounded-xl text-center text-xs text-gray-500 mb-4">
              Signature électronique du Chef de Quai apposée
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setEmargementModalItem(null)}
                className="flex-1 py-2 border border-gray-300 text-gray-700 text-xs font-semibold rounded-lg hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleValiderEmargement}
                className="flex-1 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg shadow-sm"
              >
                Valider l'émargement
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Details Fret Marchand */}
      {selectedShipment && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full shadow-2xl border border-gray-200 animate-fade-in max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between pb-3 border-b border-gray-100 mb-4">
              <div>
                <h3 className="font-bold text-gray-900 text-base">{selectedShipment.freightRef}</h3>
                <span className="text-xs text-amber-700 font-semibold">{selectedShipment.merchantName}</span>
              </div>
              <button type="button" onClick={() => setSelectedShipment(null)} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3.5 text-xs">
              <div className="p-3 bg-amber-50/70 rounded-xl">
                <div className="text-[10px] font-bold text-amber-900 uppercase">Marchand & Enseigne Partenaire</div>
                <div className="font-bold text-gray-900 mt-1 text-sm">{selectedShipment.merchantName}</div>
                <div className="text-gray-600">{selectedShipment.merchantContact} ({selectedShipment.merchantPhone})</div>
                <div className="text-amber-800 font-mono mt-0.5 font-bold">N° Bon Commande: {selectedShipment.bonCommandeNo}</div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 border border-gray-200 rounded-xl">
                  <div className="text-[10px] text-gray-500 uppercase font-semibold">Point d'enlèvement (Dépôt)</div>
                  <div className="font-bold text-gray-900 mt-1">{selectedShipment.departureHub}</div>
                </div>
                <div className="p-3 border border-gray-200 rounded-xl">
                  <div className="text-[10px] text-gray-500 uppercase font-semibold">Quai de livraison</div>
                  <div className="font-bold text-gray-900 mt-1">{selectedShipment.destinationQuai}</div>
                </div>
              </div>

              <div className="p-3 border border-gray-200 rounded-xl space-y-1">
                <div className="text-[10px] text-gray-500 uppercase font-semibold">Conditionnement & Véhicule</div>
                <div className="font-bold text-gray-900">{selectedShipment.conditionnement}</div>
                <div className="text-gray-600">
                  Tonnage : <strong>{selectedShipment.tonnage} Tonnes</strong> · Palettes : <strong>{selectedShipment.nbPalettes}</strong>
                </div>
                <div className="text-gray-600">Véhicule : {selectedShipment.vehicleAssigned}</div>
                <div className="text-indigo-600 font-semibold">Chauffeur : {selectedShipment.chauffeur} ({selectedShipment.chauffeurPhone})</div>
                <div className="text-emerald-700 font-mono font-bold mt-1">N° Scellé de sécurité : {selectedShipment.scelleNo}</div>
              </div>

              <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl flex justify-between items-center">
                <div>
                  <span className="text-[10px] text-gray-500 uppercase font-semibold block">Montant Contrat Fret</span>
                  <strong className="text-emerald-700 text-sm font-bold">{money(selectedShipment.montantFretXaf)}</strong>
                </div>
                <span className="text-xs px-2.5 py-1 rounded-full bg-slate-200 text-slate-800 font-semibold">
                  {selectedShipment.reglementStatus}
                </span>
              </div>
            </div>

            <div className="mt-5 flex gap-2 pt-3 border-t border-gray-100">
              <button
                type="button"
                onClick={() => notify(`Lettre de voiture émise pour ${selectedShipment.freightRef}`)}
                className="flex-1 py-2 border border-gray-300 rounded-lg text-xs font-semibold text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-1.5"
              >
                <PrinterIcon className="w-4 h-4" />
                <span>Lettre de Voiture</span>
              </button>
              {selectedShipment.logisticStatus !== "Émargé & Clôturé" && (
                <button
                  type="button"
                  onClick={() => {
                    setEmargementModalItem(selectedShipment);
                    setSelectedShipment(null);
                  }}
                  className="flex-1 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold"
                >
                  Émarger Déchargement
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal: Nouvelle Expédition Marchand */}
      {newOrderModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full shadow-2xl border border-gray-200 animate-fade-in max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between pb-3 border-b border-gray-100 mb-4">
              <h3 className="font-bold text-gray-900 text-base">Nouvelle Expédition Marchand / Supermarché</h3>
              <button type="button" onClick={() => setNewOrderModal(false)} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateShipment} className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-gray-700 mb-1">Enseigne Marchande / Client</label>
                  <input
                    type="text"
                    required
                    placeholder="ex: Supermarché DOVV Bastos"
                    value={newShipment.merchantName}
                    onChange={(e) => setNewShipment({ ...newShipment, merchantName: e.target.value })}
                    className="w-full h-8 px-2.5 border border-gray-200 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block font-semibold text-gray-700 mb-1">Type de marchand</label>
                  <select
                    value={newShipment.typeMarchand}
                    onChange={(e) => setNewShipment({ ...newShipment, typeMarchand: e.target.value as any })}
                    className="w-full h-8 px-2 border border-gray-200 rounded-lg bg-white"
                  >
                    <option value="Supermarché & Grande Distribution">Supermarché & Grande Distribution</option>
                    <option value="Grossiste Agro-alimentaire">Grossiste Agro-alimentaire</option>
                    <option value="Commerçant Import-Export">Commerçant Import-Export</option>
                    <option value="Quincaillerie & Matériaux">Quincaillerie & Matériaux</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-gray-700 mb-1">Contact Resp. Approvisionnement</label>
                  <input
                    type="text"
                    required
                    placeholder="Nom du responsable"
                    value={newShipment.merchantContact}
                    onChange={(e) => setNewShipment({ ...newShipment, merchantContact: e.target.value })}
                    className="w-full h-8 px-2.5 border border-gray-200 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block font-semibold text-gray-700 mb-1">Téléphone Contact</label>
                  <input
                    type="text"
                    required
                    placeholder="+237 600 00 00 00"
                    value={newShipment.merchantPhone}
                    onChange={(e) => setNewShipment({ ...newShipment, merchantPhone: e.target.value })}
                    className="w-full h-8 px-2.5 border border-gray-200 rounded-lg"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-gray-700 mb-1">Hub d'enlèvement (Départ)</label>
                  <input
                    type="text"
                    required
                    value={newShipment.departureHub}
                    onChange={(e) => setNewShipment({ ...newShipment, departureHub: e.target.value })}
                    className="w-full h-8 px-2.5 border border-gray-200 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block font-semibold text-gray-700 mb-1">Quai de déchargement (Arrivée)</label>
                  <input
                    type="text"
                    required
                    placeholder="ex: Quai réceptions DOVV Bastos"
                    value={newShipment.destinationQuai}
                    onChange={(e) => setNewShipment({ ...newShipment, destinationQuai: e.target.value })}
                    className="w-full h-8 px-2.5 border border-gray-200 rounded-lg"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="block font-semibold text-gray-700 mb-1">Corridor B2B</label>
                  <select
                    value={newShipment.corridor}
                    onChange={(e) => setNewShipment({ ...newShipment, corridor: e.target.value as any })}
                    className="w-full h-8 px-2 border border-gray-200 rounded-lg bg-white"
                  >
                    <option value="Douala → Yaoundé">Douala → Yaoundé</option>
                    <option value="Douala → Garoua">Douala → Garoua</option>
                    <option value="Douala → Bafoussam">Douala → Bafoussam</option>
                    <option value="Intra-urbain Douala">Intra-urbain Douala</option>
                  </select>
                </div>
                <div>
                  <label className="block font-semibold text-gray-700 mb-1">Tonnage (Tonnes)</label>
                  <input
                    type="number"
                    step="0.5"
                    required
                    value={newShipment.tonnage}
                    onChange={(e) => setNewShipment({ ...newShipment, tonnage: Number(e.target.value) })}
                    className="w-full h-8 px-2.5 border border-gray-200 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block font-semibold text-gray-700 mb-1">Nb. Palettes</label>
                  <input
                    type="number"
                    required
                    value={newShipment.nbPalettes}
                    onChange={(e) => setNewShipment({ ...newShipment, nbPalettes: Number(e.target.value) })}
                    className="w-full h-8 px-2.5 border border-gray-200 rounded-lg"
                  />
                </div>
              </div>

              <div>
                <label className="block font-semibold text-gray-700 mb-1">Description conditionnement</label>
                <input
                  type="text"
                  required
                  placeholder="ex: 12 Palettes filmées produits secs & conserves"
                  value={newShipment.conditionnement}
                  onChange={(e) => setNewShipment({ ...newShipment, conditionnement: e.target.value })}
                  className="w-full h-8 px-2.5 border border-gray-200 rounded-lg"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-gray-700 mb-1">Véhicule affrété</label>
                  <select
                    value={newShipment.vehicleAssigned}
                    onChange={(e) => setNewShipment({ ...newShipment, vehicleAssigned: e.target.value as any })}
                    className="w-full h-8 px-2 border border-gray-200 rounded-lg bg-white"
                  >
                    <option value="Semi-remorque 30T Bâché">Semi-remorque 30T Bâché</option>
                    <option value="Camion Frigorifique 10T">Camion Frigorifique 10T</option>
                    <option value="Camion Plateau Conteneur">Camion Plateau Conteneur</option>
                    <option value="Fourgon 5T Grand Volume">Fourgon 5T Grand Volume</option>
                  </select>
                </div>
                <div>
                  <label className="block font-semibold text-gray-700 mb-1">Montant Fret (FCFA)</label>
                  <input
                    type="number"
                    required
                    value={newShipment.montantFretXaf}
                    onChange={(e) => setNewShipment({ ...newShipment, montantFretXaf: Number(e.target.value) })}
                    className="w-full h-8 px-2.5 border border-gray-200 rounded-lg"
                  />
                </div>
              </div>

              <div className="pt-3 border-t border-gray-100 flex gap-2">
                <button
                  type="button"
                  onClick={() => setNewOrderModal(false)}
                  className="flex-1 py-2 border border-gray-300 rounded-lg text-xs font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 py-2 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white rounded-lg text-xs font-bold shadow-sm"
                >
                  Créer l'ordre de transport
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
